import { motion } from "framer-motion";
import { ArrowUpRight, Clock, MapPin, Phone } from "lucide-react";
import { ADDRESS, HOURS_DISPLAY, MAP_EMBED, ORDER_URL, PHONE, PHONE_LINK, RESERVE_URL, getOpenState } from "@/data/menu";
import { Reveal, ChapterHeading } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";

export const VisitSection = () => {
  const state = getOpenState();
  const todayJs = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Australia/Brisbane" })
  ).getDay();

  return (
    <section id="visit" className="bg-cream-deep/60" data-testid="visit-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <ChapterHeading
          number="02"
          eyebrow="Visit Us"
          title={
            <>
              Find us on <span className="italic text-terracotta">Toombul Road</span>
            </>
          }
          copy="Dine in, take away or get it delivered. Walk-ins welcome — bookings recommended on Friday and Saturday nights."
        />

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <Reveal className="relative">
            <div aria-hidden className="absolute -inset-3 rounded-[2rem] border border-sage-dark/25" />
            <motion.iframe
              title="Map to Northgate Flavour Express, 203 Toombul Rd, Northgate QLD"
              src={MAP_EMBED}
              data-testid="contact-map-iframe"
              className="relative h-full min-h-[24rem] w-full rounded-[2rem] border border-line grayscale-[35%] contrast-[1.02]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </Reveal>

          <div className="flex flex-col gap-10">
            <Reveal delay={0.08}>
              <div id="hours" className="rounded-[2rem] border border-line bg-white p-7 sm:p-9" data-testid="hours-card">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2.5 font-display text-2xl font-semibold text-forest">
                    <Clock size={20} className="text-terracotta" strokeWidth={1.75} />
                    Opening Hours
                  </h3>
                  <StatusBadge state={state} testid="opening-status-badge" />
                </div>
                <ul className="mt-6 divide-y divide-line" data-testid="hours-list">
                  {HOURS_DISPLAY.map((h) => (
                    <li
                      key={h.day}
                      data-testid={`hours-row-${h.day.toLowerCase()}`}
                      className={`flex items-baseline justify-between py-3 ${
                        h.jsDay === todayJs ? "font-semibold" : ""
                      }`}
                    >
                      <span className={`text-sm ${h.jsDay === todayJs ? "text-forest" : "text-muted-ink"}`}>
                        {h.day}
                        {h.jsDay === todayJs && (
                          <span className="ml-2 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-terracotta">
                            Today
                          </span>
                        )}
                      </span>
                      <span className={`font-display text-lg ${h.jsDay === todayJs ? "text-terracotta" : "text-forest-soft"}`}>
                        {h.time}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="address-link"
                  className="group rounded-[1.5rem] border border-line bg-white p-6 transition-all duration-300 hover:border-forest/30 hover:shadow-[0_16px_36px_-20px_rgba(24,44,32,0.45)]"
                >
                  <MapPin size={20} className="text-terracotta" strokeWidth={1.75} />
                  <p className="mt-3 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-sage-dark">Address</p>
                  <p className="mt-1.5 text-sm font-medium leading-relaxed text-forest" data-testid="address-text">
                    {ADDRESS}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-terracotta">
                    Get directions
                    <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
                <a
                  href={PHONE_LINK}
                  data-testid="phone-link"
                  className="group rounded-[1.5rem] border border-line bg-white p-6 transition-all duration-300 hover:border-forest/30 hover:shadow-[0_16px_36px_-20px_rgba(24,44,32,0.45)]"
                >
                  <Phone size={20} className="text-terracotta" strokeWidth={1.75} />
                  <p className="mt-3 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-sage-dark">Phone</p>
                  <p className="mt-1.5 font-display text-2xl font-semibold text-forest" data-testid="phone-text">
                    {PHONE}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-terracotta">
                    Call to order
                    <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.2} className="flex flex-wrap gap-4">
              <a
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-order-btn"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-semibold text-[#FFF9F1] transition-all duration-300 hover:bg-[#B8170F]"
              >
                Order Online <ArrowUpRight size={16} />
              </a>
              <a
                href={RESERVE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-reserve-btn"
                className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-7 py-3.5 text-sm font-medium text-forest transition-all duration-300 hover:border-forest hover:bg-forest hover:text-[#FFF9F1]"
              >
                Reserve a Table
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
